import { Col, Divider, Row, Typography } from 'antd';
import { StyledStack } from 'app/components/Stack';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import RangeDatePickerCustom from 'app/pages/NewAccount/CheckTickets/RangeDatePickerCustom';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { formatNumber } from 'utils/helper';
import Filter, {
  createDateObjectByTimezone,
  FilterFormValues,
  MAIN_TABS,
  transformFilterValues,
} from './Filter';
import StyledTable from 'app/components/StyledTable';
import StyledPagination from 'app/components/StyledPagination';
import useTitleSize from 'app/components/StyledTable/useTitleSize';
import moment from 'moment-timezone';
import { ReactComponent as WalletIcon } from 'app/assets/img/wallet.svg';
import useWindowSize from 'app/hooks/useWindowSize';
import { useFormContext } from 'react-hook-form';
import { PageStateContext, SCREEN } from '..';
import useFetchData from 'app/hooks/useFetchData';
import { getTransactionHistoryApi } from 'app/services/agentApi';
import { useHistory } from 'react-router-dom';
import * as _ from 'lodash';
import useGetDetailTicket from 'app/pages/NewAccount/hooks/useGetDetailTicket';
import StyledTabs from 'app/components/StyledTabs';
import TransactionList from './TransactionList';
enum COMMISSION_TAB {
  B2B = 'b2b',
  API = 'api',
}
const OverviewScreen: React.FC = () => {
  const { getValues, setValue, reset } = useFormContext<FilterFormValues>();
  const { tableRef, setScreen, setPageLoading, anchorPointerRef, filterRef } =
    useContext(PageStateContext);

  const history = useHistory();

  const { windowSize } = useWindowSize();
  const href = window.location.href;
  const searchParams = new URL(href).searchParams;
  const [isLoadingHeaderData, setIsLoadingHeaderData] = useState(true);
  const [mainTab, setMainTab] = useState(
    (searchParams.get('screen') as MAIN_TABS) ?? MAIN_TABS.OVERVIEW,
  );
  const [monthTab, setMonthTab] = useState(COMMISSION_TAB.B2B);
  const [yearTab, setYearTab] = useState(COMMISSION_TAB.B2B);
  const [mouTab, setMouTab] = useState(COMMISSION_TAB.B2B);

  const translations = useSelector(selectTranslations);

  const { result: overviewData, onFetchData: onFetchOverviewData } =
    useFetchData({
      actionApi: getTransactionHistoryApi,
      IIFE: false,
      onBefore: () => setPageLoading(true),
      onFinally: () => setPageLoading(false),
      payloadData: {
        dateFrom: getValues('dateFrom')?.format('DD/MM/YYYY'),
        dateTo: (getValues('dateTo') ?? getValues('dateFrom'))?.format(
          'DD/MM/YYYY',
        ),
        documentNumber: getValues('documentNumber') ?? '',
        transactionType: getValues('transactionType') ?? '',
        paymentMethod: getValues('paymentMethod') ?? '',
        page: getValues('page'),
        limit: getValues('limit'),
        typeReport: getValues('typeReport'),
      },
    });
  useEffect(() => {
    if (mainTab) {
      setValue('tab', mainTab);
    }
  }, [mainTab]);
  const { onClickShowDetail, isGetingDetail } = useGetDetailTicket({
    screen: '/agent/transaction-history',
    setIsLoading: setPageLoading,
  });
  const dataSource = useMemo(() => {
    const details = overviewData?.details || [];
    const paginator = overviewData?.paginator || {};
    return details.map((item, index) => ({
      order: (paginator?.current - 1) * paginator?.numItemsPerPage + index + 1,
      created_at: item.created_at,
      documentNumber: item?.documentNumber,
      orderCode: item?.submitNumber,
      rootOrder: item?.original_order,
      transactionType: item?.transaction_type_name,
      transactionName: item?.so_type_name,
      pttt: item?.payMethod,
      paymentMethodRoot: item?.original_payMethod,
      amountPayment: +item?.amount,
      penaltyFee: item?.penalty_amount,
      isOrder: item?.isOrder || false,
      invoices: item?.invoices || {},
    }));
  }, [overviewData?.details, overviewData?.paginator]);

  const titleColumnSizes = useTitleSize({
    title: [
      'b2b.txt.agent-table-order',
      'b2b.txt.time',
      'b2b.txt.order-id',
      'b2b.txt.order-code',
      'b2b.txt.root-order',
      'b2b.txt.transaction-type',
      'b2b.txt.transaction_name',
      'b2c.txt.pttt',
      'b2b.txt.payment-method-root',
      'b2b.txt.amount-payment',
      'b2b.txt.penaltyFee',
      'b2b.txt.invoiceNumber',
    ],
  });

  const columns = useMemo(
    () => [
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.agent-table-order']}
          </div>
        ),
        key: 'order',
        dataIndex: 'order',
        render: value => {
          return (
            <div
              style={{
                textAlign: 'left',
                minWidth: titleColumnSizes['b2b.txt.agent-table-order'],
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.time']}
          </div>
        ),
        dataIndex: 'created_at',
        key: 'created_at',
        render: value => {
          return (
            <StyledStack
              justifyContent={'start'}
              style={{ minWidth: titleColumnSizes['b2b.txt.time'] }}
            >
              <div style={{ textAlign: 'left' }}>
                <Typography>{value}</Typography>
              </div>
            </StyledStack>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.order-id']}
          </div>
        ),
        dataIndex: 'documentNumber',
        key: 'documentNumber',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.order-id'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.order-code']}
          </div>
        ),
        dataIndex: 'orderCode',
        key: 'orderCode',
        render: (value, record) => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.order-code'],
              ...(record?.isOrder
                ? { color: '#F8B117', cursor: 'pointer' }
                : {}),
            }}
            onClick={() => (record?.isOrder ? onClickShowDetail(value) : {})}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.root-order']}
          </div>
        ),
        dataIndex: 'rootOrder',
        key: 'rootOrder',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.root-order'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.transaction-type']}
          </div>
        ),
        dataIndex: 'transactionType',
        key: 'transactionType',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.transaction-type'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.transaction_name']}
          </div>
        ),
        dataIndex: 'transactionName',
        key: 'transactionName',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.transaction_name'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2c.txt.pttt']}
          </div>
        ),
        dataIndex: 'pttt',
        key: 'pttt',
        render: value => (
          <div
            style={{
              textAlign: 'center',
              minWidth: titleColumnSizes['b2c.txt.pttt'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.txt.payment-method-root']}
          </div>
        ),
        dataIndex: 'paymentMethodRoot',
        key: 'paymentMethodRoot',
        render: value => (
          <div
            style={{
              textAlign: 'center',
              minWidth: titleColumnSizes['b2b.txt.payment-method-root'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'right' }}>
            {translations?.['b2b.txt.amount-payment']}
          </div>
        ),
        dataIndex: 'amountPayment',
        key: 'amountPayment',
        render: value => (
          <div
            style={{
              textAlign: 'right',
              minWidth: titleColumnSizes['b2b.txt.amount-payment'],
            }}
          >
            {_.isNil(value)
              ? null
              : `${formatNumber(value).replaceAll(',', '.')}đ`}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'right' }}>
            {translations?.['b2b.txt.penaltyFee']}
          </div>
        ),
        dataIndex: 'penaltyFee',
        key: 'penaltyFee',
        render: value => (
          <div
            style={{
              textAlign: 'right',
              minWidth: titleColumnSizes['b2b.txt.penaltyFee'],
            }}
          >
            {_.isNil(value)
              ? null
              : `${formatNumber(value).replaceAll(',', '.')}đ`}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.invoiceNumber']}
          </div>
        ),
        dataIndex: 'invoices',
        key: 'invoices',
        render: value => {
          const invoiceList = (value?.seq || '')
            .toString()
            .split(',')
            .map(item => item.trim())
            .filter(Boolean);
          return (
            <div
              style={{
                textAlign: 'left',
                minWidth: titleColumnSizes['b2b.txt.invoiceNumber'],
                whiteSpace: 'pre-line',
              }}
            >
              {invoiceList.map((inv, index) => (
                <div key={index}>{inv}</div>
              ))}
            </div>
          );
        },
      },
    ],
    [titleColumnSizes, translations],
  );

  const spanLayout = useMemo(() => {
    if (windowSize.width >= 992) return 8;
    if (windowSize.width >= 768) return 12;
    return 24;
  }, [windowSize.width]);
  const spanLayoutFinance = useMemo(() => {
    if (windowSize.width >= 1216) return 6;
    if (windowSize.width >= 992) return 8;
    if (windowSize.width >= 768) return 12;
    return 24;
  }, [windowSize.width]);
  const spanBonusBalance = useMemo(() => {
    if (windowSize.width >= 1216) return 12;
    if (windowSize.width >= 992) return 12;
    if (windowSize.width >= 768) return 24;
    return 24;
  }, [windowSize.width]);

  const handleViewIncurredDetail = () => {
    setScreen(SCREEN.TRANSACTION_DETAIL);
    reset(filterRef.current);
    window.scrollTo(0, 0);
    history.replace('/agent/transaction-history?screen=detail');
  };

  const handleChangePage = async (page: number, pageSize: number) => {
    setValue('page', page);
    setValue('limit', pageSize);

    filterRef.current = {
      ...filterRef.current,
      page,
      limit: pageSize,
    };

    onFetchOverviewData({
      payloadData: transformFilterValues({
        ...filterRef.current,
        page,
        limit: pageSize,
      }),
      callback: {
        onSuccess: () => {
          if (anchorPointerRef?.current) {
            const top =
              anchorPointerRef.current.getBoundingClientRect().top +
              window.scrollY -
              130;
            window.scrollTo({ top, behavior: 'smooth' });
          }

          tableRef?.current?.querySelector('.ant-table-body')?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },
      },
    });
  };

  const handleChangeTime = () => {
    const isSameDateFrom = filterRef.current.dateFrom?.isSame(
      getValues('dateFrom'),
      'day',
    );

    const isSameDateTo = filterRef.current.dateTo?.isSame(
      getValues('dateTo') ?? getValues('dateFrom'),
      'day',
    );

    if (isSameDateFrom && isSameDateTo) return;

    setValue('page', 1);

    filterRef.current = {
      ...filterRef.current,
      tab: getValues('tab'),
      dateFrom: getValues('dateFrom'),
      dateTo: getValues('dateTo') ?? getValues('dateFrom'),
      page: 1,
    };

    onFetchOverviewData({
      payloadData: transformFilterValues({
        ...filterRef.current,
        dateFrom: getValues('dateFrom'),
        dateTo: getValues('dateTo') ?? getValues('dateFrom'),
        page: 1,
      }),
      callback: {
        onBefore: () => {
          setIsLoadingHeaderData(true);
          setPageLoading(true);
        },
        onSuccess: () => {
          tableRef?.current?.querySelector('.ant-table-body')?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },
        onFinally: () => {
          setIsLoadingHeaderData(false);
          setPageLoading(false);
        },
      },
    });
  };

  useEffect(() => {
    onFetchOverviewData({
      callback: {
        onFinally: () => {
          setIsLoadingHeaderData(false);
          setPageLoading(false);
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledStack
      rowGap={'24px'}
      direction={'column'}
      padding={{ lg: '30px', md: '30px', xs: '16px' }}
      style={{
        background: 'white',
        borderRadius: 20,
        boxShadow: '0px 20px 20px 0px #0000000D',
        width: '100%',
      }}
    >
      <Typography
        style={{
          fontWeight: 600,
          fontSize: 20,
          color: 'black',
          textTransform: 'uppercase',
        }}
      >
        {translations?.['b2b.menu.agent-transaction-history'] ?? ''}
      </Typography>
      <div style={{ width: '100%' }}>
        <StyledTabs
          activeKey={mainTab}
          onChange={tab => {
            const targetTab = tab as MAIN_TABS;
            setMainTab(targetTab);
            history.replace(
              targetTab === MAIN_TABS.TRANSACTION_LIST
                ? '/agent/transaction-history?screen=transaction-list'
                : `/agent/transaction-history`,
            );
          }}
        >
          <StyledTabs.TabPane
            key={MAIN_TABS.OVERVIEW}
            tab={translations?.['b2b.txt.historyOverview']?.toUpperCase() ?? ''}
          >
            <StyledStack rowGap={'24px'} direction={'column'}>
              <Typography
                style={{
                  fontWeight: 600,
                  fontSize: 20,
                  color: 'black',
                  textTransform: 'uppercase',
                }}
              >
                {translations?.['b2b.txt.transactionHistoryOverview'] ?? ''}
              </Typography>

              <div className="d-flex align-items-end" style={{ width: '100%' }}>
                <RangeDatePickerContainer>
                  <SmallTitle>
                    {translations?.['b2b.txt.transactionDate'] ?? ''}
                  </SmallTitle>
                  <RangeDatePickerCustom
                    placeholder={
                      translations ? translations['b2b.txt.ticket-expried'] : ''
                    }
                    startDateInit={createDateObjectByTimezone(
                      getValues('dateFrom'),
                    )}
                    endDateInit={createDateObjectByTimezone(
                      getValues('dateTo'),
                    )}
                    onChange={([start, end]) => {
                      setValue('dateFrom', start ? moment(start) : null);
                      setValue('dateTo', end ? moment(end) : null);
                    }}
                    minDate={null}
                    onCalendarClose={handleChangeTime}
                  />
                </RangeDatePickerContainer>
              </div>

              <div style={{ width: '100%' }}>
                <Row gutter={[30, 30]}>
                  <Col span={spanLayout}>
                    <div className="d-flex">
                      <Title>
                        {translations?.['b2b.txt.openingBalance'] ?? ''}
                      </Title>
                    </div>
                    <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                      <Col span={24}>
                        <SmallTitle>
                          {translations?.['b2b.txt.openingDebitBalance'] ?? ''}
                        </SmallTitle>
                        <Cell>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(overviewData?.openingBalanceDebit)
                            ? null
                            : `${formatNumber(
                                overviewData?.openingBalanceDebit,
                              ).replaceAll(',', '.')}đ`}
                        </Cell>
                      </Col>
                      <Col span={24}>
                        <SmallTitle>
                          {translations?.['b2b.txt.openingCreditBalance'] ?? ''}
                        </SmallTitle>
                        <Cell>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(overviewData?.openingBalanceCredit)
                            ? null
                            : `${formatNumber(
                                overviewData?.openingBalanceCredit,
                              ).replaceAll(',', '.')}đ`}
                        </Cell>
                      </Col>
                    </Row>
                  </Col>
                  <Col
                    span={spanLayout}
                    style={{
                      borderLeft:
                        windowSize.width >= 768 ? 'solid 1px #eaeaeb' : 'none',
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <Title>
                        {translations?.['b2b.txt.incurredAmount'] ?? ''}
                      </Title>
                      <Title
                        hover
                        style={{ fontWeight: 500, color: '#fab317' }}
                        onClick={handleViewIncurredDetail}
                      >
                        {translations?.['b2b.txt.detail'] ?? ''}
                      </Title>
                    </div>
                    <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                      <Col span={24}>
                        <SmallTitle>
                          {translations?.['b2b.txt.debitIncurred'] ?? ''}
                        </SmallTitle>
                        <Cell>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(overviewData?.amountIncurredDebit)
                            ? null
                            : `${formatNumber(
                                overviewData?.amountIncurredDebit,
                              ).replaceAll(',', '.')}đ`}
                        </Cell>
                      </Col>
                      <Col span={24}>
                        <SmallTitle>
                          {translations?.['b2b.txt.creditIncurred'] ?? ''}
                        </SmallTitle>
                        <Cell>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(overviewData?.amountIncurredCredit)
                            ? null
                            : `${formatNumber(
                                overviewData?.amountIncurredCredit,
                              ).replaceAll(',', '.')}đ`}
                        </Cell>
                      </Col>
                    </Row>
                  </Col>
                  <Col
                    span={spanLayout}
                    style={{
                      borderLeft:
                        windowSize.width >= 992 ? 'solid 1px #eaeaeb' : 'none',
                    }}
                  >
                    <div className="d-flex">
                      <Title>
                        {translations?.['b2b.txt.endingBalance'] ?? ''}
                      </Title>
                    </div>
                    <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                      <Col span={24}>
                        <SmallTitle>
                          {translations?.['b2b.txt.debitBalance'] ?? ''}
                        </SmallTitle>
                        <Cell>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(overviewData?.closingBalanceDebit)
                            ? null
                            : `${formatNumber(
                                overviewData?.closingBalanceDebit,
                              ).replaceAll(',', '.')}đ`}
                        </Cell>
                      </Col>
                      <Col span={24}>
                        <SmallTitle>
                          {translations?.['b2b.txt.creditBalance'] ?? ''}
                        </SmallTitle>
                        <Cell>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(overviewData?.closingBalanceCredit)
                            ? null
                            : `${formatNumber(
                                overviewData?.closingBalanceCredit,
                              ).replaceAll(',', '.')}đ`}
                        </Cell>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>

              <FinancialOverview>
                <Title style={{ marginBottom: 24 }}>
                  {translations?.['b2b.txt.financialOverview'] ?? ''}
                </Title>
                <Row>
                  <Col span={spanLayoutFinance}>
                    <SmallTitle style={{ color: '#818188' }}>
                      <WalletIcon style={{ marginRight: 10 }} />
                      {translations?.['b2b.txt.totalOutstandingToDate'] ?? ''}
                    </SmallTitle>
                    <FinancialItemNum>
                      {isLoadingHeaderData
                        ? translations?.['b2b.txt.loadingData']
                        : _.isNil(overviewData?.totalAmount)
                        ? null
                        : `${formatNumber(overviewData?.totalAmount).replaceAll(
                            ',',
                            '.',
                          )}đ`}
                    </FinancialItemNum>
                  </Col>

                  <Col span={spanLayoutFinance}>
                    <SmallTitle style={{ color: '#818188' }}>
                      <WalletIcon style={{ marginRight: 10 }} />
                      {translations?.['b2b.txt.agent-deposit-balance'] ?? ''}
                    </SmallTitle>
                    <FinancialItemNum>
                      {isLoadingHeaderData
                        ? translations?.['b2b.txt.loadingData']
                        : _.isNil(overviewData?.amount)
                        ? null
                        : `${formatNumber(overviewData?.amount).replaceAll(
                            ',',
                            '.',
                          )}đ`}
                    </FinancialItemNum>
                  </Col>
                  <Col span={spanLayoutFinance}>
                    <SmallTitle style={{ color: '#818188' }}>
                      {translations?.['b2b.txt.remainingCreditLimit'] ?? ''}
                    </SmallTitle>
                    <FinancialItemNum>
                      {isLoadingHeaderData
                        ? translations?.['b2b.txt.loadingData']
                        : _.isNil(overviewData?.availableCredit)
                        ? null
                        : `${formatNumber(
                            overviewData?.availableCredit,
                          ).replaceAll(',', '.')}đ`}
                    </FinancialItemNum>
                  </Col>
                  <Col span={spanLayoutFinance}>
                    <SmallTitle style={{ color: '#818188' }}>
                      {translations?.['b2b.txt.pendingBalance'] ?? ''}
                    </SmallTitle>
                    <FinancialItemNum>
                      {isLoadingHeaderData
                        ? translations?.['b2b.txt.loadingData']
                        : _.isNil(overviewData?.onHoldAmount)
                        ? null
                        : `${formatNumber(
                            overviewData?.onHoldAmount,
                          ).replaceAll(',', '.')}đ`}
                    </FinancialItemNum>
                  </Col>
                  <>
                    <Divider className="my-0" />
                    <Col span={spanBonusBalance}>
                      <div className="d-flex align-items-center gap-3">
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.monthBonusBalance'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(overviewData?.rewardWalletMonthBalance)
                            ? null
                            : `${formatNumber(
                                overviewData?.rewardWalletMonthBalance || 0,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </div>
                    </Col>
                    <Col span={spanBonusBalance}></Col>
                  </>
                  {overviewData?.isChannelSeperated === 1 ? (
                    <>
                      <Col span={24}>
                        <StyledTabs
                          activeKey={monthTab}
                          onChange={tab => {
                            const targetTab = tab as COMMISSION_TAB;
                            setMonthTab(targetTab);
                          }}
                        >
                          <StyledTabs.TabPane
                            key={COMMISSION_TAB.B2B}
                            tab={'B2B'}
                          />
                          <StyledTabs.TabPane
                            key={COMMISSION_TAB.API}
                            tab={'API'}
                          />
                        </StyledTabs>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.dailyLimit'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                monthTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMonthApi
                                      ?.limitAmount
                                  : overviewData?.commissionMonthB2b
                                      ?.limitAmount,
                              )
                            ? 'N/A'
                            : `${formatNumber(
                                monthTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMonthApi
                                      ?.limitAmount
                                  : overviewData?.commissionMonthB2b
                                      ?.limitAmount,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.dailyAvailableLimit'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                monthTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMonthApi
                                      ?.availableAmount
                                  : overviewData?.commissionMonthB2b
                                      ?.availableAmount,
                              )
                            ? 'N/A'
                            : `${formatNumber(
                                monthTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMonthApi
                                      ?.availableAmount
                                  : overviewData?.commissionMonthB2b
                                      ?.availableAmount,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.bonusExpirationDate'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                (monthTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMonthApi?.validFrom
                                  : overviewData?.commissionMonthB2b
                                      ?.validFrom) &&
                                  (monthTab === COMMISSION_TAB.API
                                    ? overviewData?.commissionMonthApi?.validTo
                                    : overviewData?.commissionMonthB2b
                                        ?.validTo),
                              )
                            ? 'N/A'
                            : `${moment(
                                (monthTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMonthApi?.validFrom
                                  : overviewData?.commissionMonthB2b?.validFrom
                                ).toString(),
                                'YYYYMMDD',
                              ).format('DD/MM/YYYY')} - ${moment(
                                (monthTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMonthApi?.validTo
                                  : overviewData?.commissionMonthB2b?.validTo
                                ).toString(),
                                'YYYYMMDD',
                              ).format('DD/MM/YYYY')}`}
                        </FinancialItemNum>
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.dailyLimit'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                overviewData?.commissionMonthB2b?.limitAmount,
                              )
                            ? 'N/A'
                            : `${formatNumber(
                                overviewData?.commissionMonthB2b?.limitAmount ||
                                  0,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.dailyAvailableLimit'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                overviewData?.commissionMonthB2b
                                  ?.availableAmount,
                              )
                            ? 'N/A'
                            : `${formatNumber(
                                overviewData?.commissionMonthB2b
                                  ?.availableAmount || 0,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.bonusExpirationDate'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                overviewData?.commissionMonthB2b?.validFrom &&
                                  overviewData?.commissionMonthB2b?.validTo,
                              )
                            ? 'N/A'
                            : `${moment(
                                (overviewData?.commissionMonthB2b?.validFrom).toString(),
                                'YYYYMMDD',
                              ).format('DD/MM/YYYY')} - ${moment(
                                (overviewData?.commissionMonthB2b?.validTo).toString(),
                                'YYYYMMDD',
                              ).format('DD/MM/YYYY')}`}
                        </FinancialItemNum>
                      </Col>
                    </>
                  )}
                  <>
                    <Divider className="my-0" />
                    <Col span={spanBonusBalance}>
                      <div className="d-flex align-items-center gap-3">
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.yearBonusBalance'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(overviewData?.rewardWalletYearBalance)
                            ? null
                            : `${formatNumber(
                                overviewData?.rewardWalletYearBalance || 0,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </div>
                    </Col>
                    <Col span={spanBonusBalance}></Col>
                  </>
                  {overviewData?.isChannelSeperated === 1 ? (
                    <>
                      <Col span={24}>
                        <StyledTabs
                          activeKey={yearTab}
                          onChange={tab => {
                            const targetTab = tab as COMMISSION_TAB;
                            setYearTab(targetTab);
                          }}
                        >
                          <StyledTabs.TabPane
                            key={COMMISSION_TAB.B2B}
                            tab={'B2B'}
                          />
                          <StyledTabs.TabPane
                            key={COMMISSION_TAB.API}
                            tab={'API'}
                          />
                        </StyledTabs>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.dailyLimit'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                yearTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionYearApi?.limitAmount
                                  : overviewData?.commissionYearB2b
                                      ?.limitAmount,
                              )
                            ? 'N/A'
                            : `${formatNumber(
                                yearTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionYearApi?.limitAmount
                                  : overviewData?.commissionYearB2b
                                      ?.limitAmount,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.dailyAvailableLimit'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                yearTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionYearApi
                                      ?.availableAmount
                                  : overviewData?.commissionYearB2b
                                      ?.availableAmount,
                              )
                            ? 'N/A'
                            : `${formatNumber(
                                yearTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionYearApi
                                      ?.availableAmount
                                  : overviewData?.commissionYearB2b
                                      ?.availableAmount,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.bonusExpirationDate'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                (yearTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionYearApi?.validFrom
                                  : overviewData?.commissionYearB2b
                                      ?.validFrom) &&
                                  (yearTab === COMMISSION_TAB.API
                                    ? overviewData?.commissionYearApi?.validTo
                                    : overviewData?.commissionYearB2b?.validTo),
                              )
                            ? 'N/A'
                            : `${moment(
                                (yearTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionYearApi?.validFrom
                                  : overviewData?.commissionYearB2b?.validFrom
                                ).toString(),
                                'YYYYMMDD',
                              ).format('DD/MM/YYYY')} - ${moment(
                                (yearTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionYearApi?.validTo
                                  : overviewData?.commissionYearB2b?.validTo
                                ).toString(),
                                'YYYYMMDD',
                              ).format('DD/MM/YYYY')}`}
                        </FinancialItemNum>
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.dailyLimit'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                overviewData?.commissionYearB2b?.limitAmount,
                              )
                            ? 'N/A'
                            : `${formatNumber(
                                overviewData?.commissionYearB2b?.limitAmount ||
                                  0,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.dailyAvailableLimit'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                overviewData?.commissionYearB2b
                                  ?.availableAmount,
                              )
                            ? 'N/A'
                            : `${formatNumber(
                                overviewData?.commissionYearB2b
                                  ?.availableAmount || 0,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.bonusExpirationDate'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                overviewData?.commissionYearB2b?.validFrom &&
                                  overviewData?.commissionYearB2b?.validTo,
                              )
                            ? 'N/A'
                            : `${moment(
                                (overviewData?.commissionYearB2b?.validFrom).toString(),
                                'YYYYMMDD',
                              ).format('DD/MM/YYYY')} - ${moment(
                                (overviewData?.commissionYearB2b?.validTo).toString(),
                                'YYYYMMDD',
                              ).format('DD/MM/YYYY')}`}
                        </FinancialItemNum>
                      </Col>
                    </>
                  )}
                  <>
                    <Divider className="my-0" />
                    <Col span={spanBonusBalance}>
                      <div className="d-flex align-items-center gap-3">
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.mouBonusBalance'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(overviewData?.rewardWalletMouBalance)
                            ? null
                            : `${formatNumber(
                                overviewData?.rewardWalletMouBalance || 0,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </div>
                    </Col>
                    <Col span={spanBonusBalance}></Col>
                  </>
                  {overviewData?.isChannelSeperated === 1 ? (
                    <>
                      <Col span={24}>
                        <StyledTabs
                          activeKey={mouTab}
                          onChange={tab => {
                            const targetTab = tab as COMMISSION_TAB;
                            setMouTab(targetTab);
                          }}
                        >
                          <StyledTabs.TabPane
                            key={COMMISSION_TAB.B2B}
                            tab={'B2B'}
                          />
                          <StyledTabs.TabPane
                            key={COMMISSION_TAB.API}
                            tab={'API'}
                          />
                        </StyledTabs>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.dailyLimit'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                mouTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMouApi?.limitAmount
                                  : overviewData?.commissionMouB2b?.limitAmount,
                              )
                            ? 'N/A'
                            : `${formatNumber(
                                mouTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMouApi?.limitAmount
                                  : overviewData?.commissionMouB2b?.limitAmount,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.dailyAvailableLimit'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                mouTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMouApi
                                      ?.availableAmount
                                  : overviewData?.commissionMouB2b
                                      ?.availableAmount,
                              )
                            ? 'N/A'
                            : `${formatNumber(
                                mouTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMouApi
                                      ?.availableAmount
                                  : overviewData?.commissionMouB2b
                                      ?.availableAmount,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.bonusExpirationDate'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                (mouTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMouApi?.validFrom
                                  : overviewData?.commissionMouB2b
                                      ?.validFrom) &&
                                  (mouTab === COMMISSION_TAB.API
                                    ? overviewData?.commissionMouApi?.validTo
                                    : overviewData?.commissionMouB2b?.validTo),
                              )
                            ? 'N/A'
                            : `${moment(
                                (mouTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMouApi?.validFrom
                                  : overviewData?.commissionMouB2b?.validFrom
                                ).toString(),
                                'YYYYMMDD',
                              ).format('DD/MM/YYYY')} - ${moment(
                                (mouTab === COMMISSION_TAB.API
                                  ? overviewData?.commissionMouApi?.validTo
                                  : overviewData?.commissionMouB2b?.validTo
                                ).toString(),
                                'YYYYMMDD',
                              ).format('DD/MM/YYYY')}`}
                        </FinancialItemNum>
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.dailyLimit'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                overviewData?.commissionMouB2b?.limitAmount,
                              )
                            ? 'N/A'
                            : `${formatNumber(
                                overviewData?.commissionMouB2b?.limitAmount ||
                                  0,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.dailyAvailableLimit'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                overviewData?.commissionMouB2b?.availableAmount,
                              )
                            ? 'N/A'
                            : `${formatNumber(
                                overviewData?.commissionMouB2b
                                  ?.availableAmount || 0,
                              ).replaceAll(',', '.')}đ`}
                        </FinancialItemNum>
                      </Col>
                      <Col span={spanLayout}>
                        <SmallTitle style={{ color: '#818188' }}>
                          {translations?.['b2b.txt.bonusExpirationDate'] ?? ''}
                        </SmallTitle>
                        <FinancialItemNum>
                          {isLoadingHeaderData
                            ? translations?.['b2b.txt.loadingData']
                            : _.isNil(
                                overviewData?.commissionMouB2b?.validFrom &&
                                  overviewData?.commissionMouB2b?.validTo,
                              )
                            ? 'N/A'
                            : `${moment(
                                (overviewData?.commissionMouB2b?.validFrom).toString(),
                                'YYYYMMDD',
                              ).format('DD/MM/YYYY')} - ${moment(
                                (overviewData?.commissionMouB2b?.validTo).toString(),
                                'YYYYMMDD',
                              ).format('DD/MM/YYYY')}`}
                        </FinancialItemNum>
                      </Col>
                    </>
                  )}
                </Row>
              </FinancialOverview>
            </StyledStack>
          </StyledTabs.TabPane>
          <StyledTabs.TabPane
            key={MAIN_TABS.TRANSACTION_LIST}
            tab={translations?.['b2b.txt.transactionList']?.toUpperCase() ?? ''}
          >
            <TransactionList />
          </StyledTabs.TabPane>
        </StyledTabs>
      </div>
    </StyledStack>
  );
};

export default OverviewScreen;

export const RangeDatePickerContainer = styled.div`
  max-width: 400px;
  width: 100%;
  .range-picker-container {
    width: 100%;
  }
  .date-picker {
    flex: 1;
    width: 100%;
    height: 36px;
  }
  .CLEAR_DATA {
    padding: 0px 12px 0px 0px;
  }
`;

const Title = styled.h5<{ hover?: boolean }>`
  font-weight: 600;
  font-size: 16px;
  color: #000000;
  margin: 0;
  margin-bottom: 8px;
  ${({ hover }) =>
    hover &&
    `
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    `}
`;

export const SmallTitle = styled.h6`
  font-weight: 500;
  font-size: 12px;
  color: #57575c;
  margin: 0;
  margin-bottom: 8px;
`;

const Cell = styled.div`
  background-color: #f7f7f8;
  border-radius: 8px;
  color: #000000;
  font-weight: 600;
  font-size: 18px;
  padding: 12px 8px;
`;

const FinancialOverview = styled.div`
  width: 100%;
  background-color: #f7f7f8;
  border-radius: 8px;
  padding: 16px;
`;

const FinancialItemNum = styled.div`
  color: #000000;
  font-weight: 600;
  font-size: 20px;
  padding: 4px 0px 12px;
`;
